import { type ReactNode, useContext } from "react";
import { SHAPE_CLASS } from "../lib/shape.js";
import {
  type SettingsLook,
  SettingsLookContext,
} from "./settings-look-context.js";

/* One setting on a settings page (PB-24). One of the three settings-page
   parts, with SectionHeader and SettingsGroup. Ported from the wireframe
   settings shell. */

type SettingRowDensity = "comfortable" | "compact";
type SettingRowVariant = "default" | "card";

export interface SettingRowProps {
  label: ReactNode;
  description?: ReactNode;
  /** The interactive control on the trailing edge (Switch, Select, …). */
  control: ReactNode;
  /** A visual at the row's leading edge, before the label: an icon, a mark. */
  leading?: ReactNode;
  /** Small badge beside the label. */
  badge?: ReactNode;
  /**
   * `comfortable` (default) stacks the control under the label below `sm`.
   * `compact` is dense type with label and control on one row at every
   * width: a label-plus-Switch row in a popover or a rail.
   */
  density?: SettingRowDensity;
  /**
   * `default` draws a divider under the row. `card` draws a boxed row with a
   * border and an 8px corner and no divider. The Direction B look is not a
   * variant: a row takes it from an enclosing `SettingsGroup
   * variant="settings"`.
   */
  variant?: SettingRowVariant;
}

const ROUNDED = SHAPE_CLASS.rounded;

/* Look (from the group) × density × variant. `default.comfortable.default` is
   today's row and `settings.comfortable.default` is settings-form.ts's ROW,
   byte for byte; tests pin both. */
const ROOT: Record<
  SettingsLook,
  Record<SettingRowDensity, Record<SettingRowVariant, string>>
> = {
  default: {
    comfortable: {
      default:
        "flex flex-col items-stretch gap-3 border-b border-pb-border/70 py-3 last:border-b-0 sm:flex-row sm:items-center sm:justify-between sm:gap-4",
      card: `flex flex-col items-stretch gap-3 ${ROUNDED} border border-pb-border/70 px-4 py-3 mt-2 first:mt-0 sm:flex-row sm:items-center sm:justify-between sm:gap-4`,
    },
    compact: {
      default:
        "flex items-center justify-between gap-3 border-b border-pb-border/70 py-2 last:border-b-0",
      card: `flex items-center justify-between gap-3 ${ROUNDED} border border-pb-border/70 px-3 py-2 mt-2 first:mt-0`,
    },
  },
  settings: {
    comfortable: {
      default:
        "flex flex-col gap-3 border-b border-pb-border/55 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-5",
      card: `flex flex-col gap-3 ${ROUNDED} border border-pb-border/55 px-4 py-4 mt-2 first:mt-0 sm:flex-row sm:items-center sm:justify-between sm:gap-5`,
    },
    compact: {
      default:
        "flex items-center justify-between gap-3 border-b border-pb-border/55 py-2",
      card: `flex items-center justify-between gap-3 ${ROUNDED} border border-pb-border/55 px-3 py-2 mt-2 first:mt-0`,
    },
  },
};

/* Dense type is one scale whichever look the group draws. */
const COMPACT_TEXT = {
  label: "text-caption font-medium text-pb-text",
  description: "mt-0.5 text-dense-2 text-pb-text-muted",
};

const TEXT: Record<
  SettingsLook,
  Record<SettingRowDensity, { label: string; description: string }>
> = {
  default: {
    comfortable: {
      label: "text-sm text-pb-text font-medium",
      description: "text-xs text-pb-text-muted mt-0.5 leading-relaxed",
    },
    compact: COMPACT_TEXT,
  },
  settings: {
    comfortable: {
      label: "text-body font-medium text-pb-text",
      description: "mt-0.5 text-body-small text-pb-text-muted",
    },
    compact: COMPACT_TEXT,
  },
};

const CONTROL: Record<SettingRowDensity, string> = {
  comfortable: "min-w-0 w-full sm:w-auto sm:shrink-0",
  compact: "shrink-0",
};

/* `cond && <Badge />` passes `false`; that is no slot, not an empty wrapper. */
const isSet = (node: ReactNode) => node != null && node !== false;

/** One setting: label + optional description, control on the trailing edge. */
export function SettingRow({
  label,
  description,
  control,
  leading,
  badge,
  density = "comfortable",
  variant = "default",
}: SettingRowProps) {
  const look = useContext(SettingsLookContext);
  const text = TEXT[look][density];
  const labelNode = <div className={text.label}>{label}</div>;
  const body = (
    <div className="min-w-0">
      {isSet(badge) ? (
        <div className="flex flex-wrap items-center gap-2">
          {labelNode}
          {badge}
        </div>
      ) : (
        labelNode
      )}
      {description ? (
        <div className={text.description}>{description}</div>
      ) : null}
    </div>
  );
  return (
    <div className={ROOT[look][density][variant]}>
      {isSet(leading) ? (
        <div className="flex min-w-0 items-center gap-3">
          {leading}
          {body}
        </div>
      ) : (
        body
      )}
      <div className={CONTROL[density]}>{control}</div>
    </div>
  );
}
